<template>
  <div>
    <!-- In server mode the database does the paging, filtering and sorting;
         server-items-length is what tells v-data-table to stop doing all three
         to the page it has just been given. -->
    <v-data-table
        v-model="selected"
        :show-select="tab < 3"
        :options.sync="tableOptions"
        :footer-props="footerProps"
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :headers="getHeader()"
        :items="users"
        :search="serverMode ? '' : debouncedSearch"
        :server-items-length="serverMode ? total : -1"
      >
        <template v-slot:top>
          <bulk-action-bar
            :count="selected.length"
            :running="bulkRunning"
            :done="bulkDone"
            :total="bulkTotal"
            :progress="bulkProgress"
            @clear="clearSelection"
          >
            <!-- the review queue takes a decision, not a status change -->
            <template v-if="tab === 2">
              <v-btn text small @click="bulkTakeAction(1)">
                <v-icon small class="me-1">mdi-check</v-icon>
                {{ $t('approve_selected') }}
              </v-btn>
              <v-btn text small @click="bulkTakeAction(2)">
                <v-icon small class="me-1">mdi-close</v-icon>
                {{ $t('reject_selected') }}
              </v-btn>
            </template>
            <template v-else>
              <v-btn text small @click="bulkSetStatus(3)">
                <v-icon small class="me-1">mdi-account-off</v-icon>
                {{ $t('suspend_selected') }}
              </v-btn>
              <v-btn text small @click="bulkSetStatus(1)">
                <v-icon small class="me-1">mdi-account-check</v-icon>
                {{ $t('activate_selected') }}
              </v-btn>
              <v-btn
                v-if="userType === 'students'"
                text
                small
                @click="$emit('map-students', selected)"
              >
                <v-icon small class="me-1">mdi-map-marker-path</v-icon>
                {{ $t('map_to_route') }}
              </v-btn>
              <!-- taking a class off a route at the end of term was three
                   clicks per child through the mapping dialog -->
              <v-btn
                v-if="userType === 'students'"
                text
                small
                @click="$emit('unmap-students', selected)"
              >
                <v-icon small class="me-1">mdi-map-marker-off</v-icon>
                {{ $t('unmap') }}
              </v-btn>
              <!-- a class on a trip, a year group on a sports day -->
              <v-btn
                v-if="userType === 'students'"
                text
                small
                @click="$emit('mark-absent', selected)"
              >
                <v-icon small class="me-1">mdi-calendar-remove-outline</v-icon>
                {{ $t('mark_away') }}
              </v-btn>
            </template>
            <v-btn v-if="userType === 'schools'" text small @click="bulkDeleteSchools">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
            <v-btn v-else text small @click="bulkDeleteUsers">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
          </bulk-action-bar>
          <v-text-field
            v-model="search"
            :label="$t('Search')"
            class="mx-4"
          ></v-text-field>
        </template>

        <template v-slot:item.status_id="{ item }">
          <v-chip
            :color="getStatusColor(item.status_id)"
            dark
          >
            {{ getStatusValue(item.status_id) }}
          </v-chip>
        </template>

        <!-- one slot, not one per user type: a second template with the same
             name silently replaces the first rather than sitting beside it -->
        <template v-slot:item.name="{ item }">
            <!-- a mistyped name meant leaving the list, opening the edit form,
                 changing one box and finding your place in the list again -->
            <v-edit-dialog
              v-if="userType === 'students'"
              :return-value.sync="item.name"
              large
              :save-text="$t('save')"
              :cancel-text="$t('cancel')"
              @open="remember(item)"
              @save="quickEdit(item, 'name')"
              @cancel="restore(item)"
            >
              <span>{{ item.name }}</span>
              <template v-slot:input>
                <v-text-field v-model="item.name" :label="$t('Name')" single-line autofocus></v-text-field>
              </template>
            </v-edit-dialog>
            <template v-else>{{item.name}}</template>
            <v-tooltip v-if="item.expired != null && item.expired"  bottom color="info">
                <template v-slot:activator="{ on }">
                <v-icon color="error" v-on="on">
                    mdi-cancel
                </v-icon>
                </template>
                <span>{{ $t('driver_documents_expired') }}</span>
            </v-tooltip>
        </template>
        <template v-if="userType === 'students'" v-slot:item.student_identification="{ item }">
          <v-edit-dialog
            :return-value.sync="item.student_identification"
            large
            :save-text="$t('save')"
            :cancel-text="$t('cancel')"
            @open="remember(item)"
            @save="quickEdit(item, 'student_identification')"
            @cancel="restore(item)"
          >
            <span :class="{ 'text--disabled': !item.student_identification }">
              {{ item.student_identification || $t('empty') }}
            </span>
            <template v-slot:input>
              <v-text-field
                v-model="item.student_identification"
                :label="$t('student_id')"
                :hint="$t('student_id_is_how_imports_match')"
                persistent-hint
                single-line
                autofocus
              ></v-text-field>
            </template>
          </v-edit-dialog>
        </template>
        <template v-if="userType === 'students'" v-slot:item.notes="{ item }">
          <v-edit-dialog
            :return-value.sync="item.notes"
            large
            :save-text="$t('save')"
            :cancel-text="$t('cancel')"
            @open="remember(item)"
            @save="quickEdit(item, 'notes')"
            @cancel="restore(item)"
          >
            <span :class="{ 'text--disabled': !item.notes }">{{ item.notes || $t('empty') }}</span>
            <template v-slot:input>
              <v-text-field v-model="item.notes" :label="$t('notes')" single-line autofocus></v-text-field>
            </template>
          </v-edit-dialog>
        </template>
        <template v-if="userType === 'guardians'" v-slot:item.role_id="{ item }">
                    <v-chip
                        dark
                        :color="item.role_id === 4? 'primary' : 'info'"
                    >
                        {{ item.role_id === 4? $t('parent') : $t('guardian') }}
                    </v-chip>
        </template>
        <template v-if="userType === 'drivers'" v-slot:item.bus="{ item }">
          <v-chip
            dark
            @click="$emit('assignBus', item)"
            :color="getBusAssignmentColor(item.bus)"
          >
            {{ item.bus != null? item.bus.license : $t('no_bus') }}
          </v-chip>
        </template>

        <template v-slot:item.balance="{ item }">
          <v-chip
            small
            dark
            :color="item.balance > 0 ? 'success' : 'error'"
            @click="$emit('add-coins', item)"
          >
            {{ item.balance || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.mapping="{ item }">
          <v-chip
            x-small
            dark
            :color="isMapped(item) ? 'success' : 'warning'"
            @click="$emit('map-students', [item])"
          >
            {{ isMapped(item) ? $t('mapped') : $t('not_mapped') }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <template v-slot:item.student_settings.morning_bus="{ item }">
          <v-tooltip v-if="tab<2 && userType === 'students' && mode === 'simple'" bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-chip
                    dark
                    @click="$emit('set-student-bus', item, 1)"
                    :color="getBusAssignmentColor(item.student_settings.morning_bus)"
                >
                    {{ item.student_settings.morning_bus != null? item.student_settings.morning_bus.license : $t('no_bus') }}
                </v-chip>
            </template>
            <span>{{ $t('set_morning_bus') }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.student_settings.afternoon_bus="{ item }">
          <v-tooltip v-if="tab<2 && userType === 'students' && mode === 'simple'" bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-chip
                    dark
                    @click="$emit('set-student-bus', item, 0)"
                    :color="getBusAssignmentColor(item.student_settings.afternoon_bus)"
                >
                    {{ item.student_settings.afternoon_bus != null? item.student_settings.afternoon_bus.license : $t('no_bus') }}
                </v-chip>
            </template>
            <span>{{ $t('set_afternoon_bus') }}</span>
          </v-tooltip>
        </template>


        <template v-slot:item.actions="{ item }">
          <v-tooltip v-if="tab==2" :key="'take-action-'+tab" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                small
                class="mx-2 primary"
                @click="$emit('view-user', item)"
              >
                <v-icon small class="mx-2">
                  mdi-card-account-mail-outline
                </v-icon>
                {{ $t('Take Action') }}
              </v-btn>
            </template>
            <span>{{ $t('approve_or_reject') }}</span>
          </v-tooltip>
          <!-- deciding on the spot: opening each person to press one of two
               buttons was the whole of the review screen for most rows -->
          <v-tooltip v-if="tab==2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" color="success" @click="$emit('approve-user', item)">
                mdi-check-circle-outline
              </v-icon>
            </template>
            <span>{{ $t('approve') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab==2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" color="error" @click="$emit('reject-user', item)">
                mdi-close-circle-outline
              </v-icon>
            </template>
            <span>{{ $t('reject') }}</span>
          </v-tooltip>
          <v-tooltip v-else :key="'eye-'+tab" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('view-user', item)">
                mdi-eye
              </v-icon>
            </template>
            <span>{{ $t('view') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab==0" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="checkIfLocationEnabled(item) && mode === 'simple'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('show-student-location', item)">
                mdi-map-marker
              </v-icon>
            </template>
            <span>{{ $t('pickup_and_drop_off_locations') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab<2"  bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('edit-user', item)">
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab<2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('suspend-user', item, users.indexOf(item))">
               {{item.status_id==3? icons.mdiAccountCheck: icons.mdiAccountOff}}
              </v-icon>
            </template>
            <span>{{item.status_id==3? $t('activate_user') : $t('suspend_user') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab<2 && userType === 'students'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('map-students', [item])">
                mdi-map-marker-path
              </v-icon>
            </template>
            <span>{{ $t('map_to_route') }}</span>
          </v-tooltip>
          <!-- set the password here and tell them what it is: the emailed link
               never arrived for anyone whose address was mistyped, and it was
               the only way back into an account -->
          <v-tooltip v-if="tab<2 && canResetPassword(item)" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('set-password', item)">
                mdi-lock-reset
              </v-icon>
            </template>
            <span>{{ $t('set_password') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'drivers' && item.bus" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('unassign-bus', item)">
                mdi-fridge-industrial-off
              </v-icon>
              <v-icon v-else-if="userType === 'drivers'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('assign-bus', item)">
                mdi-bus
              </v-icon>
            </template>
            <span>{{item.bus? $t('unassign_bus_title') : $t('assign_bus') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2 && userType === 'schools'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('add-coins', item)">
                mdi-hand-coin
              </v-icon>
            </template>
            <span>{{ $t('add_coins') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'schools'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('login-as-school', item)">
                mdi-login
              </v-icon>
            </template>
            <span>{{ $t('login_as_this_school') }}</span>
          </v-tooltip>
          <v-tooltip v-if="tab!=2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="userType === 'schools'" v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('delete-school', item)">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete_this_school') }}</span>
          </v-tooltip>
          <!-- what the family is actually sent, which the school is telephoned
               about and could not see -->
          <v-tooltip v-if="userType === 'students' && tab < 2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('student-alerts', item)">
                mdi-bell-outline
              </v-icon>
            </template>
            <span>{{ $t('alerts') }}</span>
          </v-tooltip>
          <!-- a parent telephones to say the child is not riding tomorrow -->
          <v-tooltip v-if="userType === 'students' && tab < 2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('mark-absent', [item])">
                mdi-calendar-remove-outline
              </v-icon>
            </template>
            <span>{{ $t('mark_away') }}</span>
          </v-tooltip>
          <!-- which children this guardian looks after: a link was only ever
               created as a side effect of adding a student, so a second child
               joining meant a spreadsheet import -->
          <v-tooltip v-if="userType === 'guardians' && tab<2" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" @click="$emit('manage-students', item)">
                mdi-account-child
              </v-icon>
            </template>
            <span>{{ $t('manage_students') }}</span>
          </v-tooltip>
          <!-- a duplicate or a mistyped row could only ever be suspended
               before, so it stayed in the list and still counted -->
          <v-tooltip v-if="userType !== 'schools'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-2" color="error" @click="$emit('delete-user', item)">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete_user') }}</span>
          </v-tooltip>

        </template>
        <template v-slot:no-data>
          <empty-state
            :icon="emptyState.icon"
            :title="emptyState.title"
            :body="emptyState.body"
            :secondary="emptyState.secondary"
            :secondary-to="emptyState.secondaryTo"
          ></empty-state>
        </template>
    </v-data-table>
    <bulk-result-dialog
      v-model="bulkResultDialog"
      :title="bulkResultTitle"
      :failures="bulkFailures"
    ></bulk-result-dialog>
    <confirm-notify-dialog
      v-model="bulkStatusDialog"
      :title="pendingBulkStatus === 3 ? $t('suspend_selected') : $t('activate_selected')"
      :text="pendingBulkStatus === 3
        ? $t('confirm_bulk_suspend', { count: selected.length })
        : $t('confirm_bulk_activate', { count: selected.length })"
      :confirm-text="$t('yes')"
      icon="mdi-account-multiple"
      @confirm="onBulkStatusConfirmed"
    ></confirm-notify-dialog>
  </div>
</template>

<script>
import {
  mdiAccountCheck,
  mdiAccountOff,
} from "@mdi/js";

import tableState from "@/mixins/tableState";
import bulkActions from "@/mixins/bulkActions";
import BulkActionBar from "@/components/BulkActionBar";
import BulkResultDialog from "@/components/BulkResultDialog";
import ConfirmNotifyDialog from "@/components/ConfirmNotifyDialog";
import EmptyState from "@/components/EmptyState";

export default {
  mixins: [tableState, bulkActions],
  props: {
    users: Array,
    userType: String,
    tab: Number,
    mode: String,
    //server mode: the parent fetches a page at a time and passes the total in
    serverMode: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    isLoading: { type: Boolean, default: false },
  },
  components: { BulkActionBar, BulkResultDialog, ConfirmNotifyDialog, EmptyState },
  data() {
    return {
      tableStateKey: "users",
      //what the row said before an in-place edit was opened
      editingBefore: null,
      bulkStatusDialog: false,
      pendingBulkStatus: 1,
      defaultsHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      schoolHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "coins", value: "balance" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      driverHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Bus", value: "bus" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      studentAdvancedHeaders: [
        { text: "id", value: "student_identification", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Parent Name", value: "parent.name" },
        { text: "Parent Email", value: "parent.email" },
        { text: "route", value: "mapping", sortable: false },
        //held on the student and shown nowhere: usually the class
        { text: "notes", value: "notes" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      studentSimpleHeaders: [
        { text: "id", value: "student_identification", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Parent Name", value: "parent.name" },
        { text: "Parent Email", value: "parent.email" },
        //morning bus
        { text: "Morning Bus", value: "student_settings.morning_bus" },
        //afternoon bus
        { text: "Afternoon Bus", value: "student_settings.afternoon_bus" },
        { text: "notes", value: "notes" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      parentHeaders: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "Role", value: "role_id" },
        { text: "Status", value: "status_id" },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      statuses: [
        { value: "Active", color: "success" },
        { value: "Pending", color: "warning" },
        { value: "Suspended", color: "error" },
        { value: "Under Review", color: "error" },
        { value: "Out of Coins", color: "error" },
      ],
      icons: {
        mdiAccountCheck,
        mdiAccountOff,
      },
    };
  },
  watch: {
    //server mode: every change of page, size or sort is a new question for the
    //database, and the search is answered there too
    tableOptions: {
      deep: true,
      handler() {
        if (this.serverMode) this.requestPage();
      },
    },
    debouncedSearch() {
      if (this.serverMode) this.requestPage();
    },
    tab() {
      //a different status: start at the top of it
      this.clearSelection();
    },
  },
  mounted() {
    if (this.serverMode) this.requestPage();
  },
  computed: {
    /*
     * What an empty list should say.
     *
     * "No data available" is true of every one of these screens and useful on
     * none: a school that has just signed up meets it four times and is told
     * nothing about what a guardian is, why a driver is not there yet, or
     * where a list of children comes from.
     */
    emptyState() {
      const searching = !!this.debouncedSearch;
      if (searching) {
        return {
          icon: "mdi-magnify",
          title: this.$t("nothing_matched"),
          body: this.$t("nothing_matched_hint"),
        };
      }

      //the review and suspended tabs being empty is good news, not a gap
      if (this.tab >= 2) {
        return {
          icon: "mdi-check-circle-outline",
          title: this.$t("nothing_here_is_good"),
          body: this.$t("nothing_here_is_good_hint"),
        };
      }

      const states = {
        students: {
          icon: "mdi-badge-account-outline",
          title: this.$t("no_students_yet"),
          body: this.$t("no_students_yet_hint"),
          secondary: this.$t("import_from_a_spreadsheet"),
          secondaryTo: { name: "imports", query: { kind: "students" } },
        },
        drivers: {
          icon: "mdi-account-tie-hat",
          title: this.$t("no_drivers_yet"),
          body: this.$t("no_drivers_yet_hint"),
          secondary: this.$t("import_from_a_spreadsheet"),
          secondaryTo: { name: "imports", query: { kind: "drivers" } },
        },
        guardians: {
          icon: "mdi-account-group-outline",
          title: this.$t("no_guardians_yet"),
          //a parent is created by adding their child, which nothing said
          body: this.$t("no_guardians_yet_hint"),
          secondary: this.$t("import_from_a_spreadsheet"),
          secondaryTo: { name: "imports", query: { kind: "students" } },
        },
        schools: {
          icon: "mdi-school",
          title: this.$t("no_schools_yet"),
          body: this.$t("no_schools_yet_hint"),
        },
      };

      return states[this.userType] || {
        icon: "mdi-information-outline",
        title: this.$t("noData"),
        body: "",
      };
    },
    translatedDefaultsHeaders() {
      return this.defaultsHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    translatedDriverHeaders() {
      return this.driverHeaders.map(header => ({
        ...header,
        text: this.$t(header.text.toLowerCase().replace(' ', '_'))
      }));
    },
    translatedStudentAdvancedHeaders() {
      return this.studentAdvancedHeaders.map(header => ({
        ...header,
        text: this.$t(header.text.replace(' ', '_'))
      }));
    },
    translatedStudentSimpleHeaders() {
      return this.studentSimpleHeaders.map(header => ({
        ...header,
        text: this.$t(header.text.replace(' ', '_').replace('.', '_'))
      }));
    },
    translatedParentHeaders() {
      return this.parentHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    translatedSchoolHeaders() {
      return this.schoolHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    translatedStatuses() {
      return this.statuses.map(status => ({
        ...status,
        value: this.$t(status.value.toLowerCase().replace(' ', '_'))
      }));
    },
  },
  methods: {
    /*
     * Editing in place needs the old value kept, for two reasons: Cancel has
     * to put it back, since v-model has already changed the row under the
     * dialog, and a save the server refuses - a student ID that belongs to
     * somebody else - has to leave the list showing the truth rather than the
     * value that was rejected.
     */
    remember(item) {
      this.editingBefore = {
        name: item.name,
        student_identification: item.student_identification,
        notes: item.notes,
      };
    },
    restore(item) {
      if (!this.editingBefore) return;

      item.name = this.editingBefore.name;
      item.student_identification = this.editingBefore.student_identification;
      item.notes = this.editingBefore.notes;
    },
    quickEdit(item, field) {
      const before = this.editingBefore || {};
      if (before[field] === item[field]) return;

      const payload = { student_id: item.id };
      payload[field] = item[field];

      axios
        .post("/school-students/quick-edit", payload)
        .then(() => {
          this.$notifySuccess(this.$t("saved"));
        })
        .catch((error) => {
          this.restore(item);
          this.$notifyError(error, this.$t("error_saving"));
        });
    },
    requestPage() {
      const options = this.tableOptions || {};
      this.$emit("fetch", {
        page: options.page || 1,
        //"All" is -1 in the footer; the endpoint is capped at 500 either way
        per_page: options.itemsPerPage > 0 ? options.itemsPerPage : 500,
        sort_by: (options.sortBy || [])[0] || null,
        sort_desc: (options.sortDesc || [])[0] ? 1 : 0,
        search: this.debouncedSearch || null,
      });
    },
    //only real database columns can be ordered server side; the rest say so
    //rather than sorting the page in the browser and looking like they worked
    applySortability(headers) {
      if (!this.serverMode) return headers;

      const sortable = ["id", "name", "email", "created_at", "status_id", "student_identification", "balance", "notes"];
      return headers.map((header) => ({
        ...header,
        sortable: sortable.indexOf(header.value) !== -1,
      }));
    },
    // Sends the status wanted, not a toggle. The old endpoint flipped whatever
    // it found, so a bulk "suspend" reactivated everyone in the selection who
    // was already suspended; the panel avoided that by filtering the selection
    // against its own copy of the list, which is only as fresh as the last load.
    bulkSetStatus(targetStatus) {
      if (!this.selected.length) return;
      //asks to confirm and to notify in one dialog, like the single row action
      this.pendingBulkStatus = targetStatus;
      this.bulkStatusDialog = true;
    },
    onBulkStatusConfirmed({ notify, reason }) {
      const targetStatus = this.pendingBulkStatus;
      const alreadyThere =
        targetStatus === 3
          ? this.$t("already_suspended")
          : this.$t("already_active");

      this.executeBulk(this.selected.slice(), {
        title: targetStatus === 3 ? this.$t("suspend_selected") : this.$t("activate_selected"),
        label: (user) => user.name,
        //still skipped, to save a pointless request rather than for correctness
        skip: (user) => (user.status_id === targetStatus ? alreadyThere : null),
        request: (user) =>
          axios.post("/users/set-status", {
            user_id: user.id,
            status_id: targetStatus,
            notify: notify,
            reason: reason,
          }),
        onSuccess: (user) => {
          user.status_id = targetStatus;
        },
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    // Approve or reject a whole review queue at once. A school that has just
    // opened for the year has a page of these and nothing to decide about most
    // of them; one at a time through the detail screen was the only way.
    bulkTakeAction(action) {
      const approving = action === 1;

      this.runBulk({
        items: this.selected,
        title: approving ? this.$t("approve_selected") : this.$t("reject_selected"),
        confirmText: approving
          ? this.$t("confirm_bulk_approve", { count: this.selected.length })
          : this.$t("confirm_bulk_reject", { count: this.selected.length }),
        label: (user) => user.name,
        request: (user) =>
          axios.post("/users/take-action", {
            user_id: user.id,
            reason: approving ? "Approved" : this.$t("rejected_in_bulk"),
            action: action,
          }),
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    bulkDeleteUsers() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete_users", { count: this.selected.length }),
        label: (user) => user.name,
        request: (user) => axios.post("/users/delete-user", { user_id: user.id }),
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    bulkDeleteSchools() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete_schools", { count: this.selected.length }),
        label: (school) => school.name,
        request: (school) =>
          axios.delete("/users/delete-school", { data: { school_id: school.id } }),
        onFinish: () => this.$emit("bulk-finished"),
      });
    },
    getStatusColor(status)
    {
      return this.statuses[status-1].color;
    },
    getStatusValue(status)
    {
      return this.translatedStatuses[status-1].value;
    },
    getBusAssignmentColor(bus)
    {
        return bus != null? 'info': 'error';
    },
    getHeader()
    {
      return this.applySortability(this.headersForType());
    },
    headersForType()
    {
      if(this.userType === 'drivers') return this.translatedDriverHeaders;
      if(this.userType === 'students')
      {
            if(this.mode === 'simple'){
                return this.translatedStudentSimpleHeaders;
            }else{
                return this.translatedStudentAdvancedHeaders;
            }
      }
      if(this.userType === 'guardians') return this.translatedParentHeaders;
      if(this.userType === 'schools') return this.translatedSchoolHeaders;
      return this.translatedDefaultsHeaders;
    },
    //a student rides only once they have both a pickup and a drop-off point
    isMapped(item)
    {
        const settings = item.student_settings;
        if (!settings) return false;
        return !!settings.pickup_trip_id && !!settings.drop_off_trip_id;
    },
    //students have no login of their own, so there is no password to reset
    canResetPassword(item)
    {
        return item.email != null && item.email !== '' && item.role_id !== 6;
    },
    checkIfLocationEnabled(item)
    {
        return this.userType === 'students' && item.student_settings != null && (item.student_settings.pickup_lat != null && item.student_settings.pickup_lng != null || item.student_settings.drop_off_lat != null && item.student_settings.drop_off_lng != null);
    }
  },
};
</script>
<style lang="scss">
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover{
  cursor: pointer;
  background: rgba($primary-shade--light, 0.15) !important;
}
</style>
